import { Person } from "@mui/icons-material";
import StoreIcon from "@mui/icons-material/Store";
import { Button, FormControl, MenuItem, Select, Stack, Typography } from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import { useApiContext } from "../../api/ApiProvider";
import infoIcon from "../../img/icon_info.png";
import Time from "../../shared/common/utils/time";
import type { GetAvailabilitiesResponse } from "../../shared/web/schema/getAvailabilities";
import type { Staff } from "../../shared/web/schema/getStores";
import { palette } from "../../theme";
import type { Action, Func, VoidAction } from "../../util/func";
import useAsyncUpdate from "../../util/useAsyncUpdate";
import useFiles from "../../util/useFile";
import type { ReservationState } from "../../util/useReservationState";
import FormLabel from "../FormLabel";
import { FormWrapper } from "../FormWrapper";
import Space from "../Space";
import { StyledText } from "../StyledText";
import TimeTable from "../TimeTable/TimeTable";
import getSource from "./getSource";
import storeFilter from "./storeFilter";

export default function Form1({ state }: { state: ReservationState }) {
  const source = getSource();
  const isSourceGoogle = source === "google";
  const { api } = useApiContext();
  const [stores] = useAsyncUpdate(() => api.GetStores(state.data.group.id, isSourceGoogle).then((res) => (res ? storeFilter(res.stores) : null)));
  const [availability, setAvailability] = useState<GetAvailabilitiesResponse | null>();
  const [weekOffset, setWeekOffset] = useState<number>(0);

  useEffect(() => {
    if (state.data.store) {
      (window as any).dataLayer = [];
      if (state.data.store?.google_tag_manager_id !== "") {
        TagManager.initialize({ gtmId: state.data.store.google_tag_manager_id });
      }
    }
  }, [state.data.store?.id]);

  // 店舗変更時のリセットはuseReservationStateで実装済みなのでここでは省略

  useEffect(() => {
    setAvailability(undefined);
    const { store, course, staff } = state.data;
    if (!store || !course) {
      return;
    }
    // 選択中のコースが現在の店舗に存在するか確認
    const currentStoreData = stores?.find((s) => s.store.id === store.id);
    if (!currentStoreData) {
      return;
    }
    const courseExists = currentStoreData.courses.some((c) => c.id === course.id);
    if (!courseExists) {
      // コースが店舗にないのでリセット（useReservationStateで処理されるまで待つ）
      return;
    }
    if (staff?.unavailable_courses?.includes(course.id)) {
      state.setValue("course", undefined);
    } else {
      api.GetAvailability({ course_id: course.id, staff_id: staff?.id ?? null, store_id: store.id, week_offset: weekOffset }).then(setAvailability);
    }
  }, [api, stores, state.data.store, state.data.staff, state.data.course, weekOffset]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const startAtParam = searchParams.get("startAt");
    if (availability && startAtParam) {
      // 全ての利用可能なタイムスタンプを計算してstartAtParamと照合
      for (const dayData of availability.daily_availabilities) {
        for (let i = 0; i < availability.slot_offsets_millis.length; i++) {
          if (dayData.availability_flags[i]) {
            const timestamp = dayData.start_of_day_millis + availability.slot_offsets_millis[i];
            if (timestamp.toString() === startAtParam) {
              state.setValue("date", timestamp);
              return;
            }
          }
        }
      }
    }
  }, [availability]);

  if (!stores) {
    return null;
  }

  const selectingStore = state.data.store;
  const selectingStaff = state.data.staff;
  const filteredCourses = stores.find(({ store }) => store.id === selectingStore?.id)?.courses;
  const filteredStaffs = stores.find(({ store }) => store.id === selectingStore?.id)?.staffs;

  return (
    <FormWrapper currentIndex={0} skipConfirm={state.data.store?.skip_confirm}>
      <Space h={10} />
      <Stack style={{ gap: 20 }}>
        <StyledSelect errorMessage="利用可能な店舗がありません" getMeta={(store) => ({ explanation: store.message, id: store.id, label: store.name })} items={stores.map(({ store }) => store)} label="ご希望の店舗" setValue={(store) => state.setValue("store", store)} value={state.data.store} />
        {selectingStore?.nomination_enabled && filteredStaffs && (
          <StaffSelect
            setValue={(staff) => {
              state.setValue("staff", staff);
              if (state.data.course && staff?.unavailable_courses.includes(state.data.course.id)) {
                state.setValue("course", undefined);
              }
            }}
            staffs={filteredStaffs}
            value={state.data.staff}
          />
        )}
        {selectingStore && (selectingStaff !== undefined || !selectingStore.nomination_enabled) && filteredCourses && (
          <StyledSelect
            errorMessage="利用可能なメニューがありません"
            getMeta={(course) => {
              const explanations = [];
              if (course.explanation) {
                explanations.push(course.explanation);
              }
              if (isSourceGoogle) {
                explanations.push(`所要時間：${Math.round(course.duration / Time.AMinute)}分間`);
                explanations.push(`金額：${course.price}円 (税込)`);
              }
              return { explanation: explanations.join("\n"), id: course.id, label: course.name };
            }}
            items={filteredCourses.filter((c) => {
              if (selectingStaff) {
                return !selectingStaff.unavailable_courses.includes(c.id);
              } else {
                return true;
              }
            })}
            label="ご希望のメニュー"
            setValue={(course) => state.setValue("course", course)}
            value={state.data.course}
          />
        )}
        {(() => {
          if (state.data.store && state.data.course && availability) {
            return (
              <Stack>
                <FormLabel icon={<StoreIcon />} required text="ご希望の日時" />
                <Space h={10} />
                <TimeTable availability={availability} onPaging={(page) => setWeekOffset(page)} onSelected={(date) => state.setValue("date", date)} page={weekOffset} />
              </Stack>
            );
          }
        })()}
      </Stack>
      <Space h={100} />
      {selectingStore && source === "google" && (
        <Stack style={{ alignItems: "center", background: "white", bottom: 0, boxShadow: "0 0 20px gray", left: 0, padding: "18px 32px", position: "fixed", width: "100vw" }}>
          <Stack flex={1} maxWidth={540} style={{ gap: "6px" }} width={"100%"}>
            <Stack alignItems={"center"} direction={"row"} style={{ fontSize: 15, gap: "10px" }}>
              <Typography flexShrink={0} width={84}>
                選択店舗：
              </Typography>
              <Typography flexShrink={1}>{selectingStore.name}</Typography>
            </Stack>
            <Stack alignItems={"center"} direction={"row"} style={{ gap: "10px" }}>
              <Typography flexShrink={0} width={84}>
                店舗住所：
              </Typography>
              <Typography flexShrink={1}>{`〒${selectingStore.post_code} ${selectingStore.prefecture} ${selectingStore.address_1} ${selectingStore.address_2}`}</Typography>
            </Stack>
          </Stack>
        </Stack>
      )}
    </FormWrapper>
  );
}

function StaffButton({ label, imageUrl, selected, showImage, onclick }: { label: string; imageUrl: string | undefined; selected: boolean; showImage: boolean; onclick: VoidAction }) {
  let img: ReactNode = null;
  if (showImage) {
    if (imageUrl) {
      img = <img alt={imageUrl} src={imageUrl} style={{ height: 90, objectFit: "cover", width: 90 }} />;
    } else {
      img = <Person color="disabled" sx={{ height: 90, width: 75 }} />;
    }
  }

  return (
    <Button color={selected ? "selectedButton" : "unselectedButton"} onClick={onclick} sx={{ border: `2px solid ${selected ? palette.primary.main : "lightgray"}`, minWidth: "auto", p: 1 }} variant="contained">
      <Stack alignItems="center" gap={1}>
        <Typography sx={{ fontSize: ".8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: 90 }}>{label}</Typography>
        {img}
      </Stack>
    </Button>
  );
}

function StyledSelect<T>({ label, items, value, setValue, getMeta, errorMessage }: { label: string; items: T[]; value: T | undefined; setValue: Action<T | undefined>; getMeta: Func<T, { id: number; label: string; explanation: string }>; errorMessage: string }) {
  useEffect(() => {
    if (items.length === 1 && (value === undefined || getMeta(items[0]).id !== getMeta(value).id)) {
      setValue(items[0]);
    }
  }, [items, value, setValue, getMeta]);

  return (
    <Stack>
      <FormLabel icon={<StoreIcon />} required text={label} />
      <Space h={10} />
      <FormControl fullWidth>
        <Select
          disabled={items.length <= 1}
          displayEmpty
          onChange={(e) => setValue(items.find((item) => getMeta(item).id.toString() === e.target.value.toString()))}
          renderValue={(selected) => {
            let text = errorMessage;
            if (0 < items.length) {
              const selecting = items.find((item) => getMeta(item).id.toString() === selected.toString());
              if (selecting) {
                text = getMeta(selecting).label;
              } else {
                text = "";
              }
            }
            return (
              <Typography fontSize="0.8rem" whiteSpace="normal">
                {text}
              </Typography>
            );
          }}
          sx={{ "& .MuiInputBase-input": { padding: "13px 14px" } }}
          value={value ? getMeta(value).id.toString() : ""}
        >
          {items.map((item) => (
            <MenuItem key={getMeta(item).id} sx={{ whiteSpace: "normal" }} value={getMeta(item).id}>
              {getMeta(item).label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Space h={10} />
      {(() => {
        if (value !== undefined && getMeta(value).explanation !== "") {
          const explanation = getMeta(value).explanation;
          return (
            <Stack alignItems={"flex-start"} direction={"row"} gap={1}>
              <img alt="info" height={20} src={infoIcon} style={{ flexGrow: 0, flexShrink: 0, marginTop: "1px" }} width={20} />
              <StyledText textStyle={"StoreMessage"}>{explanation}</StyledText>
            </Stack>
          );
        }
      })()}
    </Stack>
  );
}

function StaffSelect({ staffs, value, setValue }: { staffs: (Staff | null)[]; value: Staff | null | undefined; setValue: Action<Staff | null> }) {
  const { api } = useApiContext();
  const { fileUriDict } = useFiles(
    staffs.flatMap((s) => s?.file_uuid ?? []),
    api,
    [staffs],
  );

  const [error, setError] = useState<string>();

  useEffect(() => {
    if (value === undefined && error === undefined) {
      if (0 < staffs.length) {
        setValue(staffs[0]);
      } else {
        setError("選択可能なスタッフがいません。");
      }
    }
  }, [value, error, staffs, setValue]);

  if (error) {
    return <Typography>{error}</Typography>;
  }

  if (!fileUriDict || value === undefined) {
    return null;
  }

  const showImage = staffs.some((s) => s?.file_uuid);
  return (
    <Stack gap={1}>
      <FormLabel icon={<StoreIcon />} text="ご希望のスタッフ" />
      <Stack direction="row" gap={1} sx={{ overflowX: "auto", scrollbarWidth: "thin" }} whiteSpace="nowrap">
        {staffs.map((s) => {
          if (s === null) {
            return <StaffButton imageUrl={undefined} key="no-nomination" label="指名なし" onclick={() => setValue(null)} selected={value === null} showImage={showImage} />;
          }
          return <StaffButton imageUrl={s.file_uuid ? fileUriDict[s.file_uuid] : undefined} key={s.id} label={s.name} onclick={() => setValue(s)} selected={value?.id === s.id} showImage={showImage} />;
        })}
      </Stack>
      <StaffDetail imageUrl={value?.file_uuid ? fileUriDict[value.file_uuid] : undefined} staff={value} />
    </Stack>
  );
}

function StaffDetail({ staff, imageUrl }: { staff: Staff | null; imageUrl: string | undefined }) {
  if (staff === null) {
    return null;
  }

  let img = <></>;
  if (staff.file_uuid) {
    img = <img alt="" src={imageUrl} style={{ flexShrink: 0, height: 164, maxWidth: "25vw", objectFit: "contain", width: 164 }} />;
  }
  return (
    <Stack alignItems="center" direction="row">
      {img}
      <Stack color={"gray"} flexGrow={1} p={1}>
        <Typography color={"black"} fontSize={20}>
          {staff.name}
        </Typography>
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Typography>{staff.role}</Typography>
          <Stack alignItems="center" direction="row">
            <Typography>指名料:</Typography>
            <Typography color="red" fontSize={20}>
              ￥{staff.price.toLocaleString()}
            </Typography>
          </Stack>
        </Stack>
        <Stack pt={1} visibility={staff.message !== "" ? "visible" : "hidden"}>
          <Typography>スタッフからの一言</Typography>
          <Typography>{staff.message}</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}
